import { noOp } from 'common/Constants';
import { ClientEvents } from 'common/events/ClientEvents';
import { EXP_VAR_TREATMENT, ExperimentNames } from 'common/experiments/ExperimentDefinitions';
import { DateString, formatDateTimeUrlDate, urlDateToDateTime } from 'common/UrlDate';
import React, { useCallback, useMemo } from 'react';
import { useVariant } from 'src/experimentation/useVariant';
import { useScroller } from 'src/shared/hooks/useScroller';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Colors } from 'web-common/src/shared/styles';
import { Button } from './Button';
import {
  DatePickerContainer,
  DatePickerWeekWrapper,
  LeftButtonContainer,
  LeftCarouselGradient,
  RightButtonContainer,
  RightCarouselGradient,
  ScrollButton,
  StyledArrowLeft,
  StyledArrowRight,
} from './styles';
import { DatePickerFlexibleProps, DatePickerProps, DatePickerWeekProps } from './types';
import { generateThreeWeekCalendar, getFormattedAvailabilityDates, isDateTomorrow, isPreviousDate } from './utils';

export const WeekRow: React.FC<DatePickerWeekProps> = (props) => {
  const { availableDeliveryDays, isShefMenu, selectedDate, timeZone, toggleDate, ...rest } = props;
  const hasAvailableDays = !!availableDeliveryDays.length;
  const datePickerData = useMemo(
    () =>
      availableDeliveryDays.map((availableDay) => {
        const dateTime = urlDateToDateTime(availableDay.date, timeZone);
        const dateString = formatDateTimeUrlDate(dateTime);
        const isTomorrow = isDateTomorrow(availableDay.date);
        const isPastDate = isPreviousDate(dateTime) && !isTomorrow;

        return {
          active: selectedDate === dateString,
          date: `${dateTime.monthShort} ${dateTime.day}`,
          isShefMenu,
          isPastDate,
          isUnavailable: availableDay.count < 1,
          onClick: isPastDate ? noOp : () => toggleDate(dateString),
          weekday: isTomorrow ? 'Tomorrow' : `${dateTime.weekdayShort}`,
          urlDate: availableDay.date,
          availability: availableDay.count === 1 ? `${availableDay.count} shef` : `${availableDay.count} shefs`,
        };
      }),
    [availableDeliveryDays, isShefMenu, timeZone, selectedDate, toggleDate]
  );

  return hasAvailableDays ? (
    <DatePickerWeekWrapper {...rest}>
      {datePickerData.map(({ urlDate, ...buttonData }) => (
        <Button key={urlDate} data-cy={`date-picker-date-btn-${urlDate}`} {...buttonData} />
      ))}
    </DatePickerWeekWrapper>
  ) : null;
};

const Flexible: React.FC<DatePickerFlexibleProps> = ({ isShefMenu, selectedDate, totalShefs, toggleFlexible }) => {
  const datePickerData = {
    active: selectedDate === undefined,
    date: '',
    isShefMenu,
    isPastDate: false,
    isUnavailable: false,
    onClick: toggleFlexible,
    weekday: "I'm Flexible",
    availability: totalShefs === 1 ? `${totalShefs} shef` : `${totalShefs} shefs`,
  };

  return (
    <DatePickerWeekWrapper>
      <Button data-cy={`date-picker-date-btn-flexible`} {...datePickerData} />
    </DatePickerWeekWrapper>
  );
};

export const List: React.FC<DatePickerProps> = (props) => {
  const { availableDeliveryDays, isShefMenu, timeZone, selectedDate, totalShefs, onDateChange } = props;
  const availableDates = useMemo(
    () =>
      availableDeliveryDays.length
        ? getFormattedAvailabilityDates({ availableDeliveryDays })
        : generateThreeWeekCalendar(),
    [availableDeliveryDays]
  );
  const tracker = useTracker();
  const { variant } = useVariant(ExperimentNames.ANY_DAY_DELIVERY, false);
  const isTreatment = variant?.name === EXP_VAR_TREATMENT;

  const { scrollRef, scrollLeft, scrollRight, canScrollLeft, canScrollRight, onScroll } = useScroller(700);

  const handleDateChange = useCallback(
    (newDate: DateString) => {
      tracker.track(ClientEvents.SUBMIT_DELIVERY_DAY_FILTER, { dates: [newDate] });
      onDateChange(newDate);
    },
    [onDateChange, tracker]
  );

  const toggleDate = useCallback(
    (date: DateString) => {
      const isDateAlreadySelected = selectedDate === date;
      const newDate = isDateAlreadySelected ? undefined : date;
      if (newDate) {
        handleDateChange(newDate);
      }
    },
    [handleDateChange, selectedDate]
  );

  const toggleFlexible = useCallback(() => {
    tracker.track(ClientEvents.SUBMIT_DELIVERY_DAY_FILTER, { dates: [] });
    onDateChange();
  }, [onDateChange, tracker]);

  const sharedWeekRowProps = { selectedDate, isShefMenu, timeZone, toggleDate };

  const [thisWeekDates, nextWeekDates, followingWeekDates] = useMemo(
    () => [availableDates.slice(0, 7), availableDates.slice(7, 14), availableDates.slice(14, 21)],
    [availableDates]
  );

  return (
    <>
      <LeftCarouselGradient isVisible={canScrollLeft} />
      <RightCarouselGradient isVisible={canScrollRight} />
      <LeftButtonContainer isVisible={canScrollLeft} onClick={scrollLeft}>
        <ScrollButton />
        <StyledArrowLeft color={Colors.RADISH} />
      </LeftButtonContainer>
      <RightButtonContainer isVisible={canScrollRight} onClick={scrollRight}>
        <ScrollButton />
        <StyledArrowRight color={Colors.RADISH} />
      </RightButtonContainer>
      <DatePickerContainer ref={scrollRef} className='no-scrollbar' onScroll={onScroll} data-cy='date-picker'>
        {isTreatment && (
          <Flexible
            isShefMenu={isShefMenu}
            selectedDate={selectedDate}
            totalShefs={totalShefs}
            toggleFlexible={toggleFlexible}
          />
        )}
        <WeekRow
          availableDeliveryDays={thisWeekDates}
          header={'This Week'}
          data-cy='date-picker-this-week'
          {...sharedWeekRowProps}
        />
        <WeekRow
          availableDeliveryDays={nextWeekDates}
          header={'Next Week'}
          data-cy='date-picker-next-week'
          {...sharedWeekRowProps}
        />
        {!!followingWeekDates.length && (
          <WeekRow availableDeliveryDays={followingWeekDates} header={'Following Week'} {...sharedWeekRowProps} />
        )}
      </DatePickerContainer>
    </>
  );
};
